import { Money } from '@classpass/protobuf-definitions/lib/web/core_types/money';
import { PartnerOrder, PartnerOrderItem } from '@classpass/protobuf-definitions/lib/web/models/partners/sku_order';
import {
  markedFulfilledTypeToJSON,
  orderItemCancellationTypeToJSON,
  processingStatusToJSON,
} from '@classpass/protobuf-definitions/lib/web/models/shared/orders/sku_order';

import { transformKeysToCamelCase } from 'helpers/core';
import { toMajorUnits } from 'helpers/currency-symbol';

import { enumToFeString, toFeLifecycleStatus } from '../shared/SkuEnumHelpers';

export { toFeLifecycleStatus };

const toMoneyWire = money => {
  if (money.currency_code !== undefined || money.currency_units !== undefined) {
    return money;
  }
  if (money.currencyCode !== undefined || money.currencyUnits !== undefined) {
    return { currency_code: money.currencyCode, currency_units: money.currencyUnits };
  }
  return Money.fromJSON(money);
};

export const normalizeMoney = money => {
  if (!money) return null;

  const wire = toMoneyWire(money);

  const currencyCode = wire.currency_code ?? wire.currencyCode;
  const currencyUnits = wire.currency_units ?? wire.currencyUnits;

  if (!currencyCode) {
    return null;
  }

  const amount = toMajorUnits(currencyCode, currencyUnits);
  if (amount === null || Number.isNaN(amount)) return null;

  return { amount, currencyCode };
};

const toIsoString = value => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  return value;
};

const normalizeSkuOrderCore = core => {
  if (!core) return null;

  return {
    orderId: core.orderId ?? null,
    userId: core.userId ?? null,
    venueId: core.venueId ?? null,
    requestedPickupTime: toIsoString(core.requestedPickupTime),
    requestedPickupIsAsap: core.requestedPickupIsAsap ?? false,
    createdOn: toIsoString(core.createdOn),
    updatedOn: toIsoString(core.updatedOn),
    processingStatus: enumToFeString(core.processingStatus, processingStatusToJSON),
    revision: core.revision ?? null,
    userMarkedPickupAt: toIsoString(core.userMarkedPickupAt),
    markedFulfilledAt: toIsoString(core.markedFulfilledAt),
    markedFulfilledType: enumToFeString(core.markedFulfilledType, markedFulfilledTypeToJSON),
  };
};

const normalizeMoneyMap = (entriesById, normalizeEntry) =>
  Object.fromEntries(Object.entries(entriesById || {}).map(([id, entry]) => [id, normalizeEntry(entry)]));

export const normalizePayableAmountDetails = details => {
  if (!details) return null;

  return {
    payableAmount: normalizeMoney(details.payable_amount),
    baseAmount: normalizeMoney(details.base_amount),
    taxAmount: normalizeMoney(details.tax_amount),
    totalAmount: normalizeMoney(details.total_amount),
    vatInclusive: details.vat_inclusive ?? null,
    items: normalizeMoneyMap(details.items, item => ({
      payableAmount: normalizeMoney(item.payable_amount),
      basePayableAmount: normalizeMoney(item.base_payable_amount),
      modifiers: normalizeMoneyMap(item.modifiers, modifier => ({
        payableAmount: normalizeMoney(modifier.payable_amount),
        basePayableAmount: normalizeMoney(modifier.base_payable_amount),
      })),
    })),
  };
};

const inheritOrderAmountsForItem = (item, orderPayableAmount) => ({
  basePayableAmount: normalizeMoney(item.basePayableAmount),
  payableAmount: orderPayableAmount,
  modifierPayableAmounts: {},
});

const normalizeItemAmounts = (item, orderItemId, { orderPayableAmount, payableAmountDetails }) => {
  if (!payableAmountDetails) return inheritOrderAmountsForItem(item, orderPayableAmount);

  const itemDetails = payableAmountDetails.items[orderItemId];
  return {
    basePayableAmount: itemDetails?.basePayableAmount ?? null,
    payableAmount: itemDetails?.payableAmount ?? null,
    modifierPayableAmounts: itemDetails?.modifiers ?? {},
  };
};

const normalizePartnerOrderItem = (apiItem, amounts) => {
  const isAlreadyParsed = apiItem?.core?.order_item_id !== undefined || apiItem?.core?.orderItemId !== undefined;
  const item = transformKeysToCamelCase(isAlreadyParsed ? apiItem : PartnerOrderItem.fromJSON(apiItem));
  const core = item.core || {};
  const orderItemId = core.orderItemId ?? null;

  return {
    core: {
      orderItemId,
      cancelledAt: toIsoString(core.cancelledAt),
      cancellationType: enumToFeString(core.cancellationType, orderItemCancellationTypeToJSON),
      itemSummary: core.itemSummary
        ? {
            itemId: core.itemSummary.itemId ?? null,
            name: core.itemSummary.name ?? null,
          }
        : null,
    },
    modifierGroups: item.modifierGroups || [],
    ...normalizeItemAmounts(item, orderItemId, amounts),
  };
};

export const normalizePartnerOrder = (apiOrder, { venueName } = {}) => {
  const parsed = PartnerOrder.fromJSON(apiOrder);
  const payableAmountDetails = normalizePayableAmountDetails(parsed.payable_amount_details);
  const order = transformKeysToCamelCase(parsed);
  const payableAmount = normalizeMoney(order.payableAmountTotal);
  const orderItems = (order.orderItems || []).map(item =>
    normalizePartnerOrderItem(item, { orderPayableAmount: payableAmount, payableAmountDetails })
  );

  return {
    core: normalizeSkuOrderCore(order.core),
    orderItems,
    userName: order.userName || null,
    lifecycleStatus: toFeLifecycleStatus(order.lifecycleStatus),
    payableAmount,
    basePayableAmount: normalizeMoney(order.basePayableAmountTotal),
    payableAmountDetails: payableAmountDetails && {
      baseAmount: payableAmountDetails.baseAmount,
      taxAmount: payableAmountDetails.taxAmount,
      totalAmount: payableAmountDetails.totalAmount,
      vatInclusive: payableAmountDetails.vatInclusive,
    },
    creditAmountTotal: order.creditAmountTotal,
    venueName: venueName || null,
  };
};
